import React, { useRef, useState } from 'react';

import { useLocale } from '@adsk/alloy-react-locale';

import FolderTree from '../FolderTree';
import { Wrapper, BodyWrapper, FolderTreeWrapper, Footer, FooterTip, FooterAction, FooterButton } from './styles';
import { setT } from '../../helpers';
import { useService } from '../../hooks';
import { FolderType, FolderModalProps, FolderPickerRef } from '../../types';

/**
 * FolderModal
 */
const FolderModal = React.forwardRef<FolderPickerRef, FolderModalProps>(
  (
    {
      show = false,
      title,
      disabled,
      cancelButtonLabel,
      confirmButtonLabel,
      confirmButtonDisabled,
      modelProps,
      multiple = false,
      onCancel = () => {},
      onSelect = () => {},
      onSelectNodesChange,
      bannerRenderer,
      footerTipRenderer,
      ...rest
    },
    forwardedRef
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<FolderType[]>([]);
    const { T, locale } = useLocale(({ locale }) => require(`../../../locale/${locale}.json`));

    const {
      state: { loading, hasError },
      dispatch
    } = useService();
    const isNormalState = !loading && !hasError;

    const handleConfirm = () => {
      onSelect({ selectedFolderKeys: selectedKeys, folders: selectedFolders });
    };

    const handleSelectKeysChange = (keys: string[], folders: FolderType[], ...args: any[]) => {
      setSelectedKeys(keys);
      setSelectedFolders(folders);
      onSelectNodesChange && onSelectNodesChange(keys, folders, ...args);
    };

    React.useEffect(() => {
      dispatch.setSdkLocale(locale);
      setT(T);
    }, [locale, dispatch, T]);

    const modalFooter = (
      <Footer>
        <FooterTip>
          {isNormalState &&
            (footerTipRenderer
              ? footerTipRenderer(selectedKeys)
              : T.translate('message.folderSelected', {
                  context: selectedKeys.length
                }))}
        </FooterTip>
        <FooterAction>
          {cancelButtonLabel && (
            <FooterButton variant="flat" data-testid="folder-picker-cancel" disabled={disabled} onClick={onCancel}>
              {cancelButtonLabel}
            </FooterButton>
          )}
          <FooterButton
            data-testid="folder-picker-confirm"
            onClick={handleConfirm}
            disabled={confirmButtonDisabled || disabled || selectedKeys.length <= 0 || !isNormalState}
          >
            {confirmButtonLabel || T.translate('buttons.select')}
          </FooterButton>
        </FooterAction>
      </Footer>
    );

    return (
      <Wrapper
        show={show}
        title={title || T.translate(`message.${multiple ? 'selectFolders' : 'selectFolder'}`)}
        backdrop="static"
        onHide={onCancel}
        footer={modalFooter}
        disabled={disabled}
        {...modelProps}
        ref={(ref) => {
          modalRef.current = ref;

          if (forwardedRef) {
            typeof forwardedRef === 'function' ? forwardedRef(ref) : (forwardedRef.current = ref);
          }
        }}
      >
        {show &&
          (bannerRenderer ? (
            <BodyWrapper>
              {bannerRenderer()}
              <FolderTreeWrapper>
                <FolderTree
                  multiple={multiple}
                  disabled={disabled}
                  {...rest}
                  container={() => modalRef.current}
                  onSelectNodesChange={handleSelectKeysChange}
                />
              </FolderTreeWrapper>
            </BodyWrapper>
          ) : (
            <FolderTree
              multiple={multiple}
              disabled={disabled}
              {...rest}
              container={() => modalRef.current}
              onSelectNodesChange={handleSelectKeysChange}
            />
          ))}
      </Wrapper>
    );
  }
);

FolderModal.displayName = 'FolderModal';

export default FolderModal;
